import { fromNode, ViewModel } from '@rehaje/core'
import { reactive, toJS } from '@rehaje/reactive'
import { keys, Path, toArr } from '@rehaje/shared'
import { createElement } from 'react'

import { WidgetView } from '../components/WidgetView'

import type {
  FormFeedback,
  SchemaMessages,
  SchemaPathMode,
  SchemaText,
  WidgetSpecific
} from '../types'
import type { SchemaNode } from './SchemaNode'
import type { ValuableModel } from './ValuableModel'
import type { ValuableNode } from './ValuableNode'
import type { WidgetModel } from './WidgetModel'
import type { TypedEvent, ViewElement } from '@rehaje/core'
import type { TFunction, TRecord } from '@rehaje/shared'

const NameSplitRE = /\s*[./]/

export class SchemaModel<
  Node extends ValuableNode = ValuableNode
> extends ViewModel {
  /**
   * 禁用
   */
  @reactive()
  __r_disabled?: boolean

  /**
   * 错误信息
   */
  @reactive()
  __r_errors?: SchemaMessages

  /**
   * 是否隐藏
   */
  @reactive()
  __r_hidden?: boolean

  /**
   * ValuableModel 特征标记
   */
  declare __r_is_valuable_model?: boolean

  /**
   * 是否为只读态
   */
  @reactive()
  __r_read_only?: boolean

  /**
   * 是否为阅读态
   */
  @reactive()
  __r_read_pretty?: boolean

  /**
   * 显示时自动恢复值，与 removeValueOnHidden 配合使用
   */
  @reactive()
  __r_recover_value_on_shown?: boolean

  /**
   * 隐藏时自动删值
   */
  @reactive()
  __r_remove_value_on_hidden?: boolean

  /**
   * 是否必填
   */
  @reactive()
  __r_required?: boolean

  /**
   * 成功提示
   */
  @reactive()
  __r_successes?: SchemaMessages

  /**
   * 警告信息
   */
  @reactive()
  __r_warnings?: SchemaMessages

  /**
   * 是否可编辑
   */
  @reactive()
  __r_write_only?: boolean

  /**
   * 输入控件
   */
  @fromNode()
  @reactive('deep')
  component?: WidgetModel

  /**
   * 输入控件包装器
   */
  @fromNode()
  @reactive('deep')
  decorator?: WidgetModel

  /**
   * 描述信息
   */
  @fromNode()
  @reactive()
  description?: SchemaText

  /**
   * 事件
   */
  @fromNode()
  events: TRecord<TFunction> = {}

  /**
   * 扩展数据
   * @deprecated
   */
  @fromNode()
  @reactive()
  extra?: TRecord

  /**
   * 表单项名称
   */
  @fromNode()
  @reactive()
  name: string = this.id

  /**
   * 数据路径解析模式
   * - 'struct': 结构化模式 (a.b -> {a:{b:..}})
   * - 'literal': 字面量模式 (a.b -> {"a.b":..})
   */
  @fromNode()
  @reactive()
  pathMode?: SchemaPathMode

  /**
   * @deprecated
   */
  @fromNode()
  @reactive()
  requiredMessage?: SchemaText

  /**
   * 标题
   */
  @fromNode()
  @reactive()
  title?: SchemaText

  /**
   * Hook 挂载器
   */
  @fromNode()
  @reactive()
  use: ViewModel[] = []

  /**
   * 校验器
   */
  @fromNode()
  @reactive()
  validator?: (value: any, values: any) => string | undefined

  /**
   * 当前模型所处父模型相对路径
   */
  get belongs() {
    return this.node.__r_belongs
  }

  /**
   * 子模型
   */
  get children(): SchemaModel[] {
    return this.__r_children.filter((child) => child instanceof SchemaModel)
  }

  /**
   * 子孙模型
   */
  get descendants(): SchemaModel[] {
    return this.children.flatMap((child) => [child, ...child.descendants])
  }

  /**
   * 禁用态
   */
  @fromNode()
  @reactive('computed')
  get disabled(): boolean {
    return this.__r_disabled ?? this.parent?.disabled ?? false
  }

  set disabled(value: boolean) {
    this.__r_disabled = Boolean(value)
  }

  /**
   * 错误消息
   */
  @fromNode()
  @reactive('computed')
  get errors(): SchemaMessages[] {
    return this.getErrors()
  }

  set errors(value: SchemaMessages[]) {
    this.__r_errors = value
  }

  get feedbacks(): FormFeedback[] {
    const feedbacks: FormFeedback[] = []
    if (this.errors.length > 0) {
      feedbacks.push({
        messages: this.errors,
        model: this as any,
        type: 'error'
      })
    }
    if (this.warnings.length > 0) {
      feedbacks.push({
        messages: this.warnings,
        model: this as any,
        type: 'warning'
      })
    }
    if (this.successes.length > 0) {
      feedbacks.push({
        messages: this.successes,
        model: this as any,
        type: 'success'
      })
    }
    return feedbacks
  }

  get form(): undefined | ValuableModel {
    return this.getForm()
  }

  /**
   * 是否隐藏
   */
  @fromNode()
  @reactive('computed')
  get hidden(): boolean {
    return this.__r_hidden || this.parent?.hidden || false
  }

  set hidden(value: boolean) {
    this.__r_hidden = Boolean(value)
  }

  /**
   * 循环索引
   */
  get index() {
    return this.__r_index
  }

  /**
   * 交互模式
   */
  @reactive('computed')
  get interactionStatus() {
    if (this.writeOnly) return 'writeOnly'
    if (this.readOnly) return 'readOnly'
    if (this.readPretty) return 'readPretty'
    if (this.disabled) return 'disabled'
    return 'editable'
  }

  /**
   * 当前字段是否处于非法态
   */
  @reactive('computed')
  get invalid() {
    return this.errors.length > 0
  }

  /**
   * 非法字段集合
   */
  @reactive('computed')
  get invalidFields() {
    return this.descendants.filter((child) => child.invalid)
  }

  /**
   * 字段命名路径
   */
  get namePath(): PropertyKey[] {
    if (this.parent) {
      return this.parent.joinChildNamePath(this)
    }
    return this.names
  }

  @reactive('computed')
  get names() {
    return this.pathMode === 'literal'
      ? [this.name]
      : String(this.name).split(NameSplitRE)
  }

  /**
   * 当前 TypedNode
   */
  get node(): Node {
    return this.__r_node as any
  }

  /**
   * 父 Schema 模型
   */
  get parent() {
    return this.parents[0]
  }

  /**
   * 父 Schema 模型集合
   */
  get parents(): SchemaModel[] {
    const parents: ViewModel[] = this.__r_parents ?? []
    return parents.filter((parent) => parent instanceof SchemaModel)
  }

  /**
   * 字段路径
   */
  get path() {
    return new Path(this.namePath)
  }

  /**
   * 只读态
   */
  @fromNode()
  @reactive('computed')
  get readOnly(): boolean {
    return this.__r_read_only ?? this.parent?.readOnly ?? false
  }

  set readOnly(value: boolean) {
    this.__r_read_only = Boolean(value)
  }

  /**
   * 阅读态
   */
  @fromNode()
  @reactive('computed')
  get readPretty(): boolean {
    return this.__r_read_pretty ?? this.parent?.readPretty ?? false
  }

  set readPretty(value: boolean) {
    this.__r_read_pretty = Boolean(value)
  }

  /**
   * 显示时自动恢复值，与 removeValueOnHidden 配合使用
   */
  @fromNode()
  @reactive('computed')
  get recoverValueOnShown(): boolean {
    return this.__r_recover_value_on_shown ?? true
  }

  set recoverValueOnShown(value: boolean) {
    this.__r_recover_value_on_shown = Boolean(value)
  }

  /**
   * 隐藏时自动删值
   */
  @reactive('computed')
  get removeValueOnHidden(): boolean {
    return this.__r_remove_value_on_hidden ?? false
  }

  set removeValueOnHidden(value: boolean) {
    this.__r_remove_value_on_hidden = Boolean(value)
  }

  /**
   * 是否必填
   */
  @fromNode()
  get required(): boolean {
    return this.__r_required ?? false
  }

  set required(value: boolean) {
    this.__r_required = Boolean(value)
  }

  /**
   * 根 Schema 模型
   */
  get root(): SchemaModel {
    return this.parent?.root ?? this
  }

  /**
   * 状态中心
   */
  get store() {
    return this.__r_store
  }

  /**
   * 成功信息
   */
  @fromNode()
  @reactive('computed')
  get successes(): SchemaMessages[] {
    return this.getSuccesses()
  }

  set successes(value: SchemaMessages[]) {
    this.__r_successes = value
  }

  /**
   * 当前字段是否处于合法态
   */
  @reactive('computed')
  get valid() {
    return this.errors.length === 0
  }

  /**
   * 校验状态
   */
  @reactive('computed')
  get validateStatus() {
    if (this.invalid) return 'error'
    if (this.warnings.length > 0) return 'warning'
    if (this.successes.length > 0) return 'success'
  }

  /**
   * 带值子模型
   */
  get valuableChildren(): ValuableModel[] {
    const children = []
    for (const child of this.getChildren<SchemaModel | WidgetModel>()) {
      if (child instanceof SchemaModel) {
        if (isValuableModel(child)) {
          children.push(child)
        } else {
          children.push(...(child.valuableChildren as ValuableModel[]))
        }
      } else if (isValuableModel(child)) {
        children.push(child)
      } else if (isWidgetModel(child)) {
        children.push(...(child.valuableChildren as ValuableModel[]))
      }
    }

    return children as ValuableModel[]
  }

  get valuableDescendants(): ValuableModel[] {
    return this.descendants.filter((child) => isValuableModel(child))
  }

  /**
   * 带值父模型
   */
  get valuableParent(): undefined | ValuableModel {
    return this.valuableParents[0]
  }

  get valuableParents(): ValuableModel[] {
    return this.parents.filter((model) =>
      isValuableModel(model)
    ) as ValuableModel[]
  }

  /**
   * 字段值路径
   */
  get valuePath(): PropertyKey[] {
    if (this.parent) {
      return this.parent.joinChildrenValuePath(this)
    }
    if (this.is(this.form)) return []
    return this.names
  }

  /**
   * 是否显示
   * 包含 hidden + removeValueOnHidden 的功能
   * @deprecated
   */
  @fromNode()
  set visible(value: boolean) {
    if (value == null || value === undefined) return
    this.__r_remove_value_on_hidden = true
    this.hidden = !value
  }

  get visible(): boolean {
    return !this.hidden
  }

  /**
   * 警告信息
   */
  @fromNode()
  @reactive('computed')
  get warnings(): SchemaMessages[] {
    return this.getWarnings()
  }

  set warnings(value: SchemaMessages[]) {
    this.__r_warnings = value
  }

  /**
   * 编辑态
   */
  @fromNode()
  @reactive('computed')
  get writeOnly(): boolean {
    return this.__r_write_only ?? this.parent?.writeOnly
  }

  set writeOnly(value: boolean) {
    this.__r_write_only = Boolean(value)
  }

  constructor(node: SchemaNode) {
    super(node)
    this.__r_registerEvents()
    this.addReaction(
      () => this.hidden,
      (hidden) => {
        if (!this.removeValueOnHidden) return
        if (hidden) {
          if (isValuableModel(this)) {
            this.clearValue()
          }
          for (const child of this.valuableChildren) child.clearValue()
        } else {
          if (isValuableModel(this)) {
            this.recoverValue()
          }
          for (const child of this.valuableChildren) child.recoverValue()
        }
      }
    )
  }

  __r_registerEvents() {
    for (const key of keys(this.events)) {
      this.addEventListener(
        key,
        (event: TypedEvent<ViewElement, any, ViewElement>) => {
          this.events[key]?.({
            ...event,
            currentTarget: event.currentTarget?.model,
            target: event.target?.model
          })
        }
      )
    }
  }

  /**
   * 连接父表单
   * @param parent
   */
  connect(parent?: ViewModel) {
    if (parent instanceof SchemaModel && !this.is(parent)) {
      const parentNode = parent.__r_node
      const form = this.form
      const formValues = toJS(form?.value)
      parentNode.replace(this.__r_node)
      if (this.form !== form) {
        if (form) form.name = parent.name
        form?.setValue(formValues)
      }
    }
  }

  getComponentProps(widget?: WidgetSpecific<string, any>) {
    const props = widget?.props
    if (isValuableModel(this)) {
      return {
        ...props,
        'data-read-pretty': this.readPretty,
        disabled: this.disabled,
        onBlur: (...args: any[]) => {
          this.onBlur(...args)
          widget?.props?.onBlur?.(...args)
        },
        onChange: (...args: any[]) => {
          this.onInput(...args)
          widget?.props?.onChange?.(...args)
        },
        onFocus: (...args: any[]) => {
          this.onFocus(...args)
          widget?.props?.onFocus?.(...args)
        },
        readOnly: this.readOnly,
        ref: widget?.ref,
        style: {
          ...props?.style
        },
        value: this.value
      }
    }

    return {
      ...props,
      ref: widget?.ref,
      style: {
        ...props?.style
      }
    }
  }

  getControlErrors() {
    return toArr(this.__r_errors).filter(Boolean)
  }

  getControlSuccesses() {
    return toArr(this.__r_successes).filter(Boolean)
  }

  getControlWarnings() {
    return toArr(this.__r_warnings).filter(Boolean)
  }

  getErrors(): SchemaMessages[] {
    return [
      ...this.getSelfErrors(),
      ...this.children.flatMap((child) => child.errors)
    ]
  }

  getForm(): undefined | ValuableModel {
    const parent = this.valuableParent?.form
    if (parent) return parent
    if (isValuableModel(this)) return this as any
    for (const child of this.children) {
      if (isValuableModel(child)) return child
    }
  }

  getSelfErrors() {
    return this.getControlErrors()
  }

  getSelfSuccesses() {
    return this.getControlSuccesses()
  }

  getSelfWarnings() {
    return this.getControlWarnings()
  }

  getSuccesses() {
    return [
      ...this.getSelfSuccesses(),
      ...this.children.flatMap((child) => child.successes)
    ]
  }

  getWarnings() {
    return [
      ...this.getSelfWarnings(),
      ...this.children.flatMap((child) => child.warnings)
    ]
  }

  joinChildNamePath(child: SchemaModel): PropertyKey[] {
    return [...this.namePath, ...child.names]
  }

  joinChildrenValuePath(child: SchemaModel): PropertyKey[] {
    return [...this.valuePath, ...child.names]
  }

  override render(children?: any): any {
    if (this.hidden) return null

    const render = (target: any, children?: any) => {
      return createElement(WidgetView, { model: this, target }, children)
    }
    return render(this.decorator, render(this.component, children))
  }

  scrollIntoView() {
    if (
      typeof this.component?.ref === 'object' &&
      this.component?.ref?.current instanceof HTMLElement
    ) {
      this.component.ref.current.scrollIntoView()
    } else if (
      typeof this.decorator?.ref === 'object' &&
      this.decorator.ref.current instanceof HTMLElement
    ) {
      this.decorator.ref.current.scrollIntoView()
    }
  }
}

function isValuableModel(model: any): model is ValuableModel {
  return !!model?.__r_is_valuable_model
}

function isWidgetModel(model: any): model is WidgetModel {
  return !!model?.__r_is_widget_model
}
